/**
 * Article image click-to-zoom lightbox (blog posts only).
 * Vanilla JS — scoped to .article-template; does not touch PDP/media galleries.
 * Zoom grows layout width (fitWidth * scale) so tiny-text screenshots become readable.
 * Pan uses translate on .article-image-lightbox__frame (img has no competing transform).
 */
(function () {
  'use strict';

  var root = document.querySelector('.article-template');
  if (!root) return;

  var MIN_SIZE = 80;
  var ZOOM_STEP = 1.35;
  var ZOOM_MIN = 1;
  var ZOOM_MAX = 8;
  var FIT_EPS = 0.02;

  var overlay = null;
  var stage = null;
  var frame = null;
  var overlayImg = null;
  var toolbar = null;
  var zoomLabel = null;
  var btnOut = null;
  var btnIn = null;
  var btnFit = null;
  var open = false;
  var lastFocus = null;

  var scale = 1;
  var tx = 0;
  var ty = 0;
  var fitWidth = 0;
  var fitHeight = 0;
  var naturalW = 0;
  var naturalH = 0;

  var pointers = Object.create(null);
  var pointerCount = 0;
  var dragging = false;
  var dragMoved = false;
  var suppressClickClose = false;
  var dragOriginX = 0;
  var dragOriginY = 0;
  var lastPX = 0;
  var lastPY = 0;
  var pinchStartDist = 0;
  var pinchStartScale = 1;
  var DRAG_THRESHOLD = 6;

  function largestFromSrcset(srcset) {
    if (!srcset) return '';
    var bestUrl = '';
    var bestW = -1;
    srcset.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (!bits[0]) return;
      var w = 0;
      if (bits[1] && /w$/i.test(bits[1])) w = parseInt(bits[1], 10) || 0;
      else if (bits[1] && /x$/i.test(bits[1])) w = (parseFloat(bits[1]) || 1) * 1000;
      else w = 1;
      if (w >= bestW) {
        bestW = w;
        bestUrl = bits[0];
      }
    });
    return bestUrl;
  }

  function bestSrc(img) {
    return (
      largestFromSrcset(img.getAttribute('srcset')) ||
      img.currentSrc ||
      img.src ||
      ''
    );
  }

  function isZoomable(img) {
    if (!img || img.tagName !== 'IMG') return false;
    if (img.closest('a[download], button, .mmd-wrap, .mermaid')) return false;
    if (img.classList.contains('article-cta__image')) return false;
    var w = img.naturalWidth || img.width || 0;
    var h = img.naturalHeight || img.height || 0;
    if (w > 0 && h > 0 && (w < MIN_SIZE || h < MIN_SIZE)) return false;
    return true;
  }

  function isAtFit() {
    return scale <= ZOOM_MIN + FIT_EPS;
  }

  function stageInnerSize() {
    if (!stage) return { w: window.innerWidth, h: window.innerHeight };
    var styles = window.getComputedStyle(stage);
    var padX =
      (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    var padY =
      (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
    var rect = stage.getBoundingClientRect();
    return {
      w: Math.max(1, rect.width - padX),
      h: Math.max(1, rect.height - padY)
    };
  }

  function computeFitSize() {
    if (!overlayImg || !naturalW || !naturalH) {
      fitWidth = 0;
      fitHeight = 0;
      return;
    }
    var box = stageInnerSize();
    var maxW = Math.min(box.w, window.innerWidth * 0.96, 1400);
    var maxH = box.h;
    var ratio = Math.min(maxW / naturalW, maxH / naturalH, 1);
    fitWidth = Math.max(1, Math.round(naturalW * ratio));
    fitHeight = Math.max(1, Math.round(naturalH * ratio));
  }

  function displaySize() {
    return {
      w: fitWidth * scale,
      h: fitHeight * scale
    };
  }

  function clampPan() {
    if (isAtFit() || !stage || fitWidth <= 0) {
      tx = 0;
      ty = 0;
      return;
    }
    var box = stageInnerSize();
    var size = displaySize();
    var maxX = Math.max(0, (size.w - box.w) / 2);
    var maxY = Math.max(0, (size.h - box.h) / 2);
    if (tx > maxX) tx = maxX;
    if (tx < -maxX) tx = -maxX;
    if (ty > maxY) ty = maxY;
    if (ty < -maxY) ty = -maxY;
  }

  function applyView(animate) {
    if (!overlayImg || !frame) return;
    clampPan();

    if (animate) {
      frame.classList.add('is-zooming');
      overlayImg.classList.add('is-zooming');
    } else {
      frame.classList.remove('is-zooming');
      overlayImg.classList.remove('is-zooming');
    }

    if (fitWidth > 0) {
      var w = fitWidth * scale;
      overlayImg.style.maxWidth = 'none';
      overlayImg.style.maxHeight = 'none';
      overlayImg.style.width = w.toFixed(2) + 'px';
      overlayImg.style.height = 'auto';
    }

    frame.style.transform =
      'translate3d(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px,0)';

    if (overlay) {
      overlay.classList.toggle('is-zoomed', !isAtFit());
    }
    if (zoomLabel) {
      zoomLabel.textContent = Math.round(scale * 100) + '%';
    }
    if (btnOut) btnOut.disabled = isAtFit();
    if (btnIn) btnIn.disabled = scale >= ZOOM_MAX - 0.001;
    if (btnFit) btnFit.disabled = isAtFit() && Math.abs(tx) < 0.5 && Math.abs(ty) < 0.5;
  }

  function setZoom(next, cx, cy, animate) {
    var prev = scale;
    var nz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    if (Math.abs(nz - prev) < 0.0001 && nz === ZOOM_MIN) {
      tx = 0;
      ty = 0;
      scale = ZOOM_MIN;
      applyView(!!animate);
      return;
    }
    if (!stage || fitWidth <= 0) {
      scale = nz;
      applyView(!!animate);
      return;
    }
    var rect = stage.getBoundingClientRect();
    var ox = typeof cx === 'number' ? cx : rect.width / 2;
    var oy = typeof cy === 'number' ? cy : rect.height / 2;
    // Point under cursor in unscaled image space before zoom
    var imgX = (ox - rect.width / 2 - tx) / prev;
    var imgY = (oy - rect.height / 2 - ty) / prev;
    scale = nz;
    tx = ox - rect.width / 2 - imgX * scale;
    ty = oy - rect.height / 2 - imgY * scale;
    if (isAtFit()) {
      tx = 0;
      ty = 0;
    }
    applyView(!!animate);
  }

  function zoomBy(factor, animate) {
    setZoom(scale * factor, null, null, animate !== false);
  }

  function resetFit(animate) {
    scale = ZOOM_MIN;
    tx = 0;
    ty = 0;
    applyView(animate !== false);
  }

  function clearInlineImgSize() {
    if (!overlayImg) return;
    overlayImg.style.maxWidth = '';
    overlayImg.style.maxHeight = '';
    overlayImg.style.width = '';
    overlayImg.style.height = '';
  }

  function measureAfterReady(done) {
    function finish() {
      naturalW = overlayImg.naturalWidth || 0;
      naturalH = overlayImg.naturalHeight || 0;
      computeFitSize();
      if (typeof done === 'function') done();
    }

    function afterDecode() {
      // Force layout so stage size is accurate while open
      void stage.offsetWidth;
      finish();
    }

    if (overlayImg.decode) {
      overlayImg
        .decode()
        .then(afterDecode)
        .catch(afterDecode);
    } else if (overlayImg.complete && overlayImg.naturalWidth) {
      afterDecode();
    } else {
      overlayImg.addEventListener('load', afterDecode, { once: true });
    }
  }

  function ensureOverlay() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'article-image-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Expanded image');
    overlay.tabIndex = -1;

    stage = document.createElement('div');
    stage.className = 'article-image-lightbox__stage';

    frame = document.createElement('div');
    frame.className = 'article-image-lightbox__frame';

    overlayImg = document.createElement('img');
    overlayImg.className = 'article-image-lightbox__img';
    overlayImg.alt = '';
    overlayImg.draggable = false;

    toolbar = document.createElement('div');
    toolbar.className = 'article-image-lightbox__toolbar';
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', 'Image zoom');
    toolbar.innerHTML =
      '<button type="button" class="article-image-lightbox__btn" data-action="zoom-out" aria-label="Zoom out" title="Zoom out">−</button>' +
      '<span class="article-image-lightbox__zoom" aria-live="polite">100%</span>' +
      '<button type="button" class="article-image-lightbox__btn" data-action="zoom-in" aria-label="Zoom in" title="Zoom in">+</button>' +
      '<button type="button" class="article-image-lightbox__btn article-image-lightbox__btn--fit" data-action="fit" aria-label="Fit to screen" title="Fit to screen">⊡</button>';

    zoomLabel = toolbar.querySelector('.article-image-lightbox__zoom');
    btnOut = toolbar.querySelector('[data-action="zoom-out"]');
    btnIn = toolbar.querySelector('[data-action="zoom-in"]');
    btnFit = toolbar.querySelector('[data-action="fit"]');

    frame.appendChild(overlayImg);
    stage.appendChild(frame);
    overlay.appendChild(stage);
    overlay.appendChild(toolbar);
    document.body.appendChild(overlay);

    toolbar.addEventListener('click', function (e) {
      e.stopPropagation();
      var btn = e.target.closest('[data-action]');
      if (!btn || btn.disabled) return;
      var action = btn.getAttribute('data-action');
      if (action === 'zoom-in') zoomBy(ZOOM_STEP);
      else if (action === 'zoom-out') zoomBy(1 / ZOOM_STEP);
      else if (action === 'fit') resetFit();
    });

    toolbar.addEventListener('pointerdown', function (e) {
      e.stopPropagation();
    });

    overlay.addEventListener('click', function (e) {
      if (toolbar.contains(e.target)) return;
      // After a pan/pinch, pointerup synthesizes a click — never treat it as close.
      if (suppressClickClose || dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        suppressClickClose = false;
        dragMoved = false;
        return;
      }
      // Explicit backdrop click (not a drag)
      if (e.target === overlay || e.target === stage) {
        closeLightbox();
        return;
      }
      // Image/frame click closes only at fit and without any drag
      if (
        (e.target === overlayImg || e.target === frame) &&
        isAtFit()
      ) {
        closeLightbox();
      }
    });

    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    stage.addEventListener('pointercancel', onPointerUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener(
      'dblclick',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (isAtFit()) {
          var rect = stage.getBoundingClientRect();
          setZoom(
            scale * ZOOM_STEP * ZOOM_STEP,
            e.clientX - rect.left,
            e.clientY - rect.top,
            true
          );
        } else {
          resetFit(true);
        }
      },
      { passive: false }
    );

    window.addEventListener('resize', function () {
      if (!open) return;
      var prevFitW = fitWidth;
      computeFitSize();
      if (prevFitW > 0 && fitWidth > 0 && Math.abs(prevFitW - fitWidth) > 0.5) {
        // Keep relative zoom; pan will re-clamp
        applyView(false);
      }
    });
  }

  function activePointers() {
    var list = [];
    for (var id in pointers) {
      if (Object.prototype.hasOwnProperty.call(pointers, id)) list.push(pointers[id]);
    }
    return list;
  }

  function dist(a, b) {
    var dx = a.x - b.x;
    var dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function onPointerDown(e) {
    if (!open) return;
    if (e.target.closest && e.target.closest('.article-image-lightbox__toolbar')) return;

    // Primary button / touch / pen only
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
    pointerCount += 1;
    dragMoved = false;
    suppressClickClose = false;
    dragOriginX = e.clientX;
    dragOriginY = e.clientY;

    try {
      stage.setPointerCapture(e.pointerId);
    } catch (err) {}

    var pts = activePointers();
    if (pts.length === 2) {
      dragging = false;
      pinchStartDist = dist(pts[0], pts[1]);
      pinchStartScale = scale;
    } else if (pts.length === 1 && !isAtFit()) {
      dragging = true;
      lastPX = e.clientX;
      lastPY = e.clientY;
      overlay.classList.add('is-panning');
    }
  }

  function onPointerMove(e) {
    if (!open || !pointers[e.pointerId]) return;
    pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

    var pts = activePointers();
    if (pts.length >= 2 && pinchStartDist > 0) {
      var d = dist(pts[0], pts[1]);
      if (d > 0) {
        var rect = stage.getBoundingClientRect();
        var ox = (pts[0].x + pts[1].x) / 2 - rect.left;
        var oy = (pts[0].y + pts[1].y) / 2 - rect.top;
        setZoom(pinchStartScale * (d / pinchStartDist), ox, oy, false);
        dragMoved = true;
        suppressClickClose = true;
      }
      return;
    }

    // Track movement even before pan engages so a drag never becomes a close-click
    var odx = e.clientX - dragOriginX;
    var ody = e.clientY - dragOriginY;
    if (Math.abs(odx) > DRAG_THRESHOLD || Math.abs(ody) > DRAG_THRESHOLD) {
      dragMoved = true;
      suppressClickClose = true;
    }

    if (dragging && pts.length === 1 && !isAtFit()) {
      var dx = e.clientX - lastPX;
      var dy = e.clientY - lastPY;
      tx += dx;
      ty += dy;
      lastPX = e.clientX;
      lastPY = e.clientY;
      applyView(false);
    }
  }

  function onPointerUp(e) {
    if (pointers[e.pointerId]) {
      delete pointers[e.pointerId];
      pointerCount = Math.max(0, pointerCount - 1);
    }
    try {
      stage.releasePointerCapture(e.pointerId);
    } catch (err) {}

    var pts = activePointers();
    if (pts.length < 2) {
      pinchStartDist = 0;
    }
    if (pts.length === 1 && !isAtFit()) {
      dragging = true;
      lastPX = pts[0].x;
      lastPY = pts[0].y;
      overlay.classList.add('is-panning');
    } else if (pts.length === 0) {
      dragging = false;
      overlay.classList.remove('is-panning');
      // Keep suppressClickClose through the synthetic click that follows pointerup.
      // Click handler clears it; also expire after a beat if no click fires.
      if (suppressClickClose || dragMoved) {
        window.setTimeout(function () {
          suppressClickClose = false;
          dragMoved = false;
        }, 320);
      }
    }
  }

  function onWheel(e) {
    if (!open) return;
    e.preventDefault();
    var rect = stage.getBoundingClientRect();
    var factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    if (Math.abs(e.deltaY) < 40) {
      factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
    }
    setZoom(scale * factor, e.clientX - rect.left, e.clientY - rect.top, false);
  }

  function openLightbox(img) {
    var src = bestSrc(img);
    if (!src) return;
    ensureOverlay();
    lastFocus = document.activeElement;

    scale = ZOOM_MIN;
    tx = 0;
    ty = 0;
    fitWidth = 0;
    fitHeight = 0;
    naturalW = 0;
    naturalH = 0;
    clearInlineImgSize();
    if (frame) {
      frame.style.transform = 'translate3d(0,0,0)';
      frame.classList.remove('is-zooming');
    }
    overlayImg.classList.remove('is-zooming');
    overlayImg.style.opacity = '0';

    overlayImg.src = src;
    overlayImg.alt = img.alt || 'Expanded article image';
    overlay.classList.remove('is-open', 'is-zoomed', 'is-panning');
    void overlay.offsetWidth;
    overlay.classList.add('is-open');
    document.body.classList.add('article-lightbox-open');
    open = true;

    measureAfterReady(function () {
      if (!open) return;
      applyView(false);
      window.requestAnimationFrame(function () {
        if (!open) return;
        overlayImg.style.opacity = '1';
        frame.classList.add('is-entering');
        window.requestAnimationFrame(function () {
          if (!open) return;
          frame.classList.remove('is-entering');
          frame.classList.add('is-zooming');
          applyView(true);
        });
      });
    });

    try {
      overlay.focus();
    } catch (e) {}
  }

  function closeLightbox() {
    if (!open || !overlay) return;
    overlay.classList.remove('is-open', 'is-zoomed', 'is-panning');
    document.body.classList.remove('article-lightbox-open');
    open = false;
    pointers = Object.create(null);
    pointerCount = 0;
    dragging = false;
    dragMoved = false;
    suppressClickClose = false;
    pinchStartDist = 0;
    window.setTimeout(function () {
      if (!open && overlayImg) {
        overlayImg.removeAttribute('src');
        overlayImg.style.opacity = '';
        clearInlineImgSize();
        overlayImg.classList.remove('is-zooming');
        if (frame) {
          frame.style.transform = '';
          frame.classList.remove('is-zooming', 'is-entering');
        }
        fitWidth = 0;
        fitHeight = 0;
        naturalW = 0;
        naturalH = 0;
        scale = ZOOM_MIN;
        tx = 0;
        ty = 0;
      }
    }, 320);
    if (lastFocus && lastFocus.focus) {
      try {
        lastFocus.focus();
      } catch (e) {}
    }
  }

  function onKey(e) {
    if (!open) return;
    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      closeLightbox();
      return;
    }
    if (e.key === '+' || e.key === '=' || e.keyCode === 187) {
      e.preventDefault();
      zoomBy(ZOOM_STEP);
    } else if (e.key === '-' || e.key === '_' || e.keyCode === 189) {
      e.preventDefault();
      zoomBy(1 / ZOOM_STEP);
    } else if (e.key === '0' || e.keyCode === 48) {
      e.preventDefault();
      resetFit();
    }
  }

  function markEligible(img) {
    if (!isZoomable(img)) return;
    img.classList.add('article-zoomable');
  }

  function collect() {
    var nodes = root.querySelectorAll(
      '.article-template__content img, .article-template__hero-container img'
    );
    nodes.forEach(function (img) {
      if (img.complete && (img.naturalWidth || img.width)) {
        markEligible(img);
      } else {
        img.addEventListener(
          'load',
          function () {
            markEligible(img);
          },
          { once: true }
        );
        markEligible(img);
      }
    });
  }

  root.addEventListener('click', function (e) {
    var img = e.target.closest(
      '.article-template__content img.article-zoomable, .article-template__hero-container img.article-zoomable'
    );
    if (!img || !root.contains(img)) return;

    var link = img.closest('a');
    if (link) e.preventDefault();
    e.stopPropagation();
    openLightbox(img);
  });

  document.addEventListener('keydown', onKey);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', collect);
  } else {
    collect();
  }
})();
